import { useClient } from '@cortex/react-query';
import { formantBotAgentId } from '@cortex/types';
import { useCallback, useRef, useState } from 'react';

const threadBase = (threadId: string) =>
  `v1/persistent-agent-groups/${formantBotAgentId}/agents/${threadId}`;

/** Two soft sine notes. Rising when the call opens, falling when it closes. */
function chime(ctx: AudioContext, direction: 'up' | 'down'): Promise<void> {
  return new Promise((resolve) => {
    try {
      const notes = direction === 'up' ? [523.25, 659.25] : [659.25, 523.25];
      const now = ctx.currentTime;
      notes.forEach((frequency, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = frequency;
        const at = now + i * 0.16;
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(0.05, at + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.32);
        osc.connect(gain).connect(ctx.destination);
        osc.start(at);
        osc.stop(at + 0.34);
      });
      window.setTimeout(resolve, 520);
    } catch {
      resolve();
    }
  });
}

/**
 * Understudy-style realtime call for one bot thread. The send button starts
 * it when the composer is empty and stops it on the next click.
 */
export function useBotVoice(threadId: string) {
  const client = useClient();
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const liveRef = useRef(false);
  const [live, setLive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const meterRef = useRef(0);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const onVoiceEventRef = useRef<(channel: RTCDataChannel, raw: string) => void>(() => undefined);
  const stoppingRef = useRef(false);
  const responseInFlightRef = useRef(false);
  const audioPlayingRef = useRef(false);
  const lastBotSoundAtRef = useRef(0);
  const stopRequestedAtRef = useRef(0);
  const stopTimerRef = useRef(0);

  const setLiveState = (value: boolean) => {
    liveRef.current = value;
    setLive(value);
  };

  const stop = useCallback(() => {
    const wasLive = liveRef.current;
    setLiveState(false);
    setStarting(false);
    peerRef.current?.close();
    peerRef.current = null;
    micRef.current?.getTracks().forEach((track) => track.stop());
    micRef.current = null;
    if (audioRef.current) audioRef.current.srcObject = null;
    cancelAnimationFrame(meterRef.current);
    window.clearTimeout(stopTimerRef.current);
    stoppingRef.current = false;
    responseInFlightRef.current = false;
    audioPlayingRef.current = false;
    channelRef.current = null;
    setLevel(0);
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx && wasLive) {
      chime(ctx, 'down')
        .finally(() => {
          ctx.close().catch(() => undefined);
        })
        .catch(() => undefined);
    } else {
      ctx?.close().catch(() => undefined);
    }
  }, []);

  const start = useCallback(async () => {
    if (liveRef.current || starting) return;
    setError(null);
    setStarting(true);
    try {
      const Ctx = window.AudioContext;
      const tone = new Ctx();
      ctxRef.current = tone;
      const connection = new RTCPeerConnection();
      peerRef.current = connection;
      const audio = audioRef.current ?? new Audio();
      audio.autoplay = true;
      audioRef.current = audio;
      connection.addEventListener('track', (event) => {
        audio.srcObject = event.streams[0] ?? null;
        audio.play().catch(() => setError('Allow audio playback to hear the voice.'));
      });
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      micRef.current = mic;
      for (const track of mic.getAudioTracks()) connection.addTrack(track, mic);
      const channel = connection.createDataChannel('oai-events');
      channelRef.current = channel;
      channel.addEventListener('message', (message) => {
        onVoiceEventRef.current(channel, String(message.data));
      });
      const offer = await connection.createOffer();
      await connection.setLocalDescription(offer);
      const sdp = connection.localDescription?.sdp ?? offer.sdp;
      if (!sdp) throw new Error('The browser did not produce a voice offer.');
      const body = await client.http
        .post(`v1/formant-bots/threads/${threadId}/voice`, { json: { sdp } })
        .json<{ sdp: string }>();
      if (peerRef.current !== connection) return;
      await connection.setRemoteDescription({ type: 'answer', sdp: body.sdp });
      await tone.resume().catch(() => undefined);
      for (const track of mic.getAudioTracks()) track.enabled = false;
      await chime(tone, 'up');
      if (peerRef.current !== connection) return;
      for (const track of mic.getAudioTracks()) track.enabled = true;
      setLiveState(true);
      meter(tone, mic);
    } catch (err) {
      stop();
      const name = err instanceof DOMException ? err.name : '';
      setError(
        name === 'NotAllowedError' || name === 'NotFoundError'
          ? 'Allow the microphone to talk to this bot.'
          : err instanceof Error
            ? err.message
            : 'Voice could not start.',
      );
    } finally {
      setStarting(false);
    }
  }, [client, starting, stop, threadId]);

  const readThread = useCallback(
    async (id: string, limit = 12) => {
      const detail = await client.http.get(threadBase(id)).json<{
        name?: string;
        transcript?: { type: string; text?: string; name?: string }[];
      }>();
      const lines = (detail.transcript ?? [])
        .slice(-Math.min(20, Math.max(1, limit)))
        .flatMap((chunk) => {
          if (chunk.type === 'user') return [`Person: ${chunk.text ?? ''}`];
          if (chunk.type === 'text') return [`Bot: ${chunk.text ?? ''}`];
          if (chunk.type === 'tool-call') return [`Tool: ${chunk.name ?? 'tool'}`];
          if (chunk.type === 'tool-result') return [`Result: ${chunk.text ?? ''}`];
          return [];
        });
      return {
        name: detail.name ?? 'Bot',
        text: lines.join('\n') || 'The thread has no messages yet.',
      };
    },
    [client],
  );

  const onVoiceEvent = useCallback(
    async (channel: RTCDataChannel, raw: string) => {
      let event: {
        type?: string;
        response?: {
          output?: { type?: string; name?: string; call_id?: string; arguments?: string }[];
        };
      };
      try {
        event = JSON.parse(raw);
      } catch {
        return;
      }
      if (event.type === 'response.created') responseInFlightRef.current = true;
      if (
        event.type === 'response.output_audio.delta' ||
        event.type === 'response.audio.delta' ||
        event.type === 'output_audio_buffer.started'
      ) {
        audioPlayingRef.current = true;
        lastBotSoundAtRef.current = Date.now();
      }
      if (
        event.type === 'output_audio_buffer.stopped' ||
        event.type === 'output_audio_buffer.cleared'
      ) {
        audioPlayingRef.current = false;
      }
      if (event.type !== 'response.done') return;
      responseInFlightRef.current = false;
      const calls = (event.response?.output ?? []).filter(
        (item) => item.type === 'function_call' && item.call_id,
      );
      for (const call of calls) {
        let output = 'ok';
        if (call.name === 'stop_voice') {
          output = 'Say goodbye; the call ends when you finish speaking.';
          stoppingRef.current = true;
          stopRequestedAtRef.current = Date.now();
          window.clearTimeout(stopTimerRef.current);
          stopTimerRef.current = window.setTimeout(() => stop(), 20000);
        } else if (call.name === 'read_thread') {
          let limit = 12;
          try {
            const n = Number((JSON.parse(call.arguments ?? '{}') as { limit?: number }).limit);
            if (Number.isFinite(n)) limit = n;
          } catch {
            limit = 12;
          }
          output = await readThread(threadId, limit)
            .then((row) => row.text)
            .catch(() => 'The thread could not be read.');
        } else if (call.name === 'search_history') {
          let query = '';
          let limit = 10;
          try {
            const args = JSON.parse(call.arguments ?? '{}') as { query?: string; limit?: number };
            query = String(args.query ?? '');
            if (Number.isFinite(args.limit)) limit = Number(args.limit);
          } catch {
            query = call.arguments ?? '';
          }
          const bots = await client.http
            .get('v1/formant-bots')
            .json<{ name: string; persistentAgentId: string }[]>();
          const needle = query.trim().toLowerCase();
          const hits: string[] = [];
          for (const bot of bots) {
            if (hits.length >= limit) break;
            const row = await readThread(bot.persistentAgentId, 20).catch(() => null);
            if (!row) continue;
            for (const line of row.text.split('\n')) {
              if (hits.length >= limit) break;
              if (needle && line.toLowerCase().includes(needle)) hits.push(`${bot.name}: ${line}`);
            }
          }
          output = hits.join('\n') || 'No matching lines.';
        } else if (call.name === 'send_task') {
          let task = '';
          try {
            task = String((JSON.parse(call.arguments ?? '{}') as { task?: string }).task ?? '');
          } catch {
            task = call.arguments ?? '';
          }
          await client.http.post(`${threadBase(threadId)}/events`, {
            json: { kind: 'instruction', delivery: 'now', content: task },
          });
          output = 'Sent. Not finished yet.';
          const before = await readThread(threadId)
            .then((row) => row.text)
            .catch(() => '');
          window.setTimeout(() => {
            readThread(threadId)
              .then((after) => {
                if (!liveRef.current || channel.readyState !== 'open' || after.text === before)
                  return;
                channel.send(
                  JSON.stringify({
                    type: 'conversation.item.create',
                    item: {
                      type: 'message',
                      role: 'system',
                      content: [
                        { type: 'input_text', text: `Update from the thread:\n${after.text}` },
                      ],
                    },
                  }),
                );
                channel.send(JSON.stringify({ type: 'response.create' }));
              })
              .catch(() => undefined);
          }, 8000);
        }
        if (channel.readyState === 'open') {
          channel.send(
            JSON.stringify({
              type: 'conversation.item.create',
              item: { type: 'function_call_output', call_id: call.call_id, output },
            }),
          );
        }
      }
      if (calls.length && channel.readyState === 'open') {
        channel.send(JSON.stringify({ type: 'response.create' }));
      }
    },
    [client, readThread, stop, threadId],
  );
  onVoiceEventRef.current = onVoiceEvent;

  const meter = (ctx: AudioContext, mic: MediaStream) => {
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(mic).connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    const tick = () => {
      if (!liveRef.current) return;
      analyser.getByteTimeDomainData(data);
      const now = Date.now();
      if (
        stoppingRef.current &&
        !responseInFlightRef.current &&
        !audioPlayingRef.current &&
        now - lastBotSoundAtRef.current > 2500 &&
        now - stopRequestedAtRef.current > 1000
      ) {
        stop();
        return;
      }
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = ((data[i] ?? 128) - 128) / 128;
        sum += v * v;
      }
      setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
      meterRef.current = requestAnimationFrame(tick);
    };
    meterRef.current = requestAnimationFrame(tick);
  };

  const toggle = useCallback(() => {
    if (liveRef.current) stop();
    else start().catch(() => undefined);
  }, [start, stop]);

  return { live, starting, level, error, toggle };
}
